import useAlias from '@hooks/useAlias';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Box, Typography } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { ArchivePoint } from '@src/types/Archivist';
import React, { useCallback, useMemo } from 'react';
import {
  useDataPointSearchActions,
  useDataPointSearchState,
} from '../context/DataPointSearchContext';
import {
  dataGridSx,
  EmptyStateContainer,
  emptyStateIconSx,
  resultsContainerSx,
} from '../DataPointSearch.styles';
import { useDataPointSelection } from '../hooks/useDataPointSelection';
import { useDataPointsSearch } from '../hooks/useDataPointsSearch';
import { useDeviceNameFormatter } from '../hooks/useDeviceNameFormatter';
import { formatCategoryDisplay } from '../utils';

interface ResultsGridProps {
  isModalOpen: boolean;
}

const ResultsGrid: React.FC<ResultsGridProps> = ({ isModalOpen }) => {
  const alias = useAlias();
  const state = useDataPointSearchState();
  const { setPagination, setGridFilters } = useDataPointSearchActions();
  const formatDeviceName = useDeviceNameFormatter();

  const { rows, totalRowCount, isLoading } = useDataPointsSearch(isModalOpen);

  const { currentPageSelectionModel, handleSelectionChange } =
    useDataPointSelection(rows);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'topic',
        headerName: alias('Topic'),
        flex: 3,
        minWidth: 200,
      },
      {
        field: 'deviceType',
        headerName: alias('Category'),
        flex: 1,
        minWidth: 120,
        valueFormatter: (value: string) =>
          value ? formatCategoryDisplay(value) : '',
      },
      {
        field: 'deviceId',
        headerName: alias('Device'),
        flex: 1,
        minWidth: 120,
        filterable: false,
        valueGetter: (_value, row) => formatDeviceName(row.deviceId),
      },
      {
        field: 'pointName',
        headerName: alias('Point Name'),
        flex: 2,
        minWidth: 150,
      },
    ],
    [alias, formatDeviceName],
  );

  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      setPagination({
        page: model.page,
        pageSize: model.pageSize,
      });
    },
    [setPagination],
  );

  const handleFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      setGridFilters(filterModel.items);
    },
    [setGridFilters],
  );

  const handleRowSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      handleSelectionChange(newSelectionModel, rows);
    },
    [handleSelectionChange, rows],
  );

  const getRowId = useCallback((row: ArchivePoint) => row._id.$oid, []);

  const noRowsOverlay = useCallback(
    () => (
      <EmptyStateContainer>
        <SearchOffIcon sx={emptyStateIconSx} />
        <Typography variant='body1' gutterBottom>
          {alias('No datapoints found')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {alias('Try adjusting your filters or search terms')}
        </Typography>
      </EmptyStateContainer>
    ),
    [alias],
  );

  return (
    <Box sx={resultsContainerSx}>
      <DataGridPro
        density='compact'
        pageSizeOptions={[10, 25, 50, 100]}
        pagination
        rowCount={totalRowCount}
        paginationMode='server'
        filterMode='server'
        columns={columns}
        rows={rows}
        loading={isLoading}
        paginationModel={state.pagination}
        onPaginationModelChange={handlePaginationChange}
        onFilterModelChange={handleFilterChange}
        getRowId={getRowId}
        getRowClassName={() => 'datapoint-row'}
        checkboxSelection
        rowSelectionModel={currentPageSelectionModel}
        onRowSelectionModelChange={handleRowSelectionChange}
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        sx={dataGridSx}
      />
    </Box>
  );
};

export default ResultsGrid;
